import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useReducer } from 'react'
import Router from 'next/router'
import { useAuth } from '../../../contexts/auth'
import useSessionStorage from '../../../hooks/use-session-storage'
import { ActionTypes } from '../actions/action-types'
import { generateElasticQuery, generateRouterQuery } from '../utils'
import reducer, { getInitialState } from '../reducers'
import { useQuery } from '@tanstack/react-query'
import { getMaxMileage } from '../../../api-client/search'

// ----------------------------------------------------------------------

export default function useSearchFilters() {
  const authRedirect = '/'

  const { query } = useRouter()
  const [{ userDetails }] = useAuth()
  const [userLocation, saveUserLocation] = useSessionStorage('userLocation', null)

  const [state, dispatch] = useReducer(
    reducer,
    getInitialState({ query, userDetails, userLocation, authRedirect }),
  )

  // Get Max Mileage value
  useQuery({
    queryFn: getMaxMileage,
    onSuccess: (data) => {
      if (data.maxMileage) setMaxMileage(data.maxMileage)
    },
  })

  useEffect(() => {
    Router.replace({
      pathname: Router.pathname,
      query: generateRouterQuery(state),
    })
  }, [state])

  const setPrice = useCallback((range) => {
    dispatch({ type: ActionTypes.setPrice, payload: range })
  }, [])

  const setLeaseFinance = useCallback((range) => {
    dispatch({ type: ActionTypes.setLeaseFinance, payload: range })
  }, [])

  const setMakes = useCallback((id) => {
    dispatch({ type: ActionTypes.setMakes, payload: id })
  }, [])

  const setModels = useCallback((value) => {
    dispatch({ type: ActionTypes.setModels, payload: value })
  }, [])

  const setAllModels = useCallback((models) => {
    dispatch({ type: ActionTypes.setAllModels, payload: models })
  }, [])

  const setBodyStyle = useCallback((value) => {
    dispatch({ type: ActionTypes.setBodyStyle, payload: value })
  }, [])

  const setFeature = useCallback((value) => {
    dispatch({ type: ActionTypes.setFeature, payload: value })
  }, [])

  const setYear = useCallback((range) => {
    dispatch({ type: ActionTypes.setYear, payload: range })
  }, [])

  const setMileage = useCallback((value) => {
    dispatch({ type: ActionTypes.setMileage, payload: value })
  }, [])

  const setMaxMileage = useCallback((value) => {
    dispatch({ type: ActionTypes.setMaxMileage, payload: value })
  }, [])

  const setFuel = useCallback((value) => {
    dispatch({ type: ActionTypes.setFuel, payload: value })
  }, [])

  const setEngineSize = useCallback((value) => {
    dispatch({ type: ActionTypes.setEngineSize, payload: value })
  }, [])

  const setTransmission = useCallback((value) => {
    dispatch({ type: ActionTypes.setTransmission, payload: value })
  }, [])

  const setDrivetrain = useCallback((value) => {
    dispatch({ type: ActionTypes.setDrivetrain, payload: value })
  }, [])

  const setColour = useCallback((value) => {
    dispatch({ type: ActionTypes.setColour, payload: value })
  }, [])

  const setMembership = useCallback((value) => {
    dispatch({ type: ActionTypes.setMembership, payload: value })
  }, [])

  const setLocation = useCallback(
    ({ zipCode, latitude, longitude }) => {
      saveUserLocation({ zipCode, latitude, longitude })
      dispatch({ type: ActionTypes.setLocation, payload: { zipCode, latitude, longitude } })
    },
    [saveUserLocation],
  )

  const setListingType = useCallback((value) => {
    dispatch({ type: ActionTypes.setListingType, payload: value })
  }, [])

  const setDistance = useCallback((value) => {
    dispatch({ type: ActionTypes.setDistance, payload: value })
  }, [])

  const setSearchTerms = useCallback((value) => {
    dispatch({ type: ActionTypes.setSearchTerms, payload: value })
  }, [])

  const removeTag = useCallback((name, value) => {
    dispatch({ type: ActionTypes.removeTag, name, value })
  }, [])

  const setViewType = useCallback((viewType) => {
    dispatch({ type: ActionTypes.setViewType, payload: viewType })
  }, [])

  const setPageIndex = useCallback((pageIndex) => {
    dispatch({ type: ActionTypes.setPageIndex, payload: pageIndex })
  }, [])

  const setSortType = useCallback((sortType) => {
    dispatch({ type: ActionTypes.setSortType, payload: sortType })
  }, [])

  const clearFilters = useCallback(() => {
    dispatch({ type: ActionTypes.clearFilters })
  }, [])

  const setListingVehicles = useCallback((data) => {
    dispatch({ type: ActionTypes.setListingVehicles, payload: data })
  }, [])

  const setListingAdditionalInfo = useCallback((data) => {
    dispatch({ type: ActionTypes.setListingAdditionalInfo, payload: data })
  }, [])

  return [
    useMemo(
      () => ({
        query: state,
        isQueryValid: validateState(state),
        elasticQuery: generateElasticQuery(state),
      }),
      [state],
    ),
    useMemo(
      () => ({
        setPrice,
        setLeaseFinance,
        setMakes,
        setModels,
        setAllModels,
        setBodyStyle,
        setFeature,
        setYear,
        setMileage,
        setMaxMileage,
        setFuel,
        setEngineSize,
        setTransmission,
        setDrivetrain,
        setColour,
        setMembership,
        setLocation,
        setDistance,
        setListingType,
        setSearchTerms,
        removeTag,
        setViewType,
        setPageIndex,
        setSortType,
        clearFilters,
        setListingVehicles,
        setListingAdditionalInfo,
      }),
      [
        setPrice,
        setLeaseFinance,
        setMakes,
        setModels,
        setAllModels,
        setBodyStyle,
        setFeature,
        setYear,
        setMileage,
        setMaxMileage,
        setFuel,
        setEngineSize,
        setTransmission,
        setDrivetrain,
        setColour,
        setMembership,
        setLocation,
        setDistance,
        setListingType,
        setSearchTerms,
        removeTag,
        setViewType,
        setPageIndex,
        setSortType,
        clearFilters,
        setListingVehicles,
        setListingAdditionalInfo,
      ],
    ),
  ]
}

function validateState(state) {
  return !Object.values(state.errors).some((value) => value !== null)
}
